import * as React from "react";

import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { Card, CardBody } from "kanopi-components/Card";
import { useUnderBreakpoint } from "kanopi-components/useUnderBreakpoint";
import { toCamelCase } from "kanopi-components/utils/string";

import { ParagraphMedium, LabelXSmall, LabelLarge } from "kanopi-components/typography";

import { CoverSectionIcon } from "kw/util/CoverSection/CoverSectionIcon";
import { isCoverDisabledByParent } from "kw/util/CoverSection/utils";
import { useTrackCoverAddStarted } from "kw/util/CoverSection/useTracking";

type TProps = {
  coverSection?: TCoverSection;
  riskProfileId?: string;
  riskData?: TRiskData;
  edit?: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void | unknown;
  disabled?: boolean;
  prefix?: string;
};

// NOTE: Not sure if this naming is appropriate?
const InactiveCoverSection: React.FC<TProps> = ({
  coverSection = {},
  edit,
  riskProfileId,
  riskData,
  disabled,
}) => {
  const isMobile = useUnderBreakpoint("medium");
  const trackAddStarted = useTrackCoverAddStarted();
  const { icon, title, description, product } = coverSection;
  const enableAndStartEdit = edit ? () => edit() : null;
  const disabledByParent = isCoverDisabledByParent(coverSection, false, riskData);
  const handleAdd = () => {
    enableAndStartEdit();
    trackAddStarted({ riskData, riskProfileId, cover: coverSection });
  };
  return (
    <Block display="flex" flex={1} flexDirection="column" width={["100%", "100%", "100%"]}>
      <Card
        borderWidth={"0px"}
        display="flex"
        flexDirection="column"
        overrides={{
          Block: {
            props: {
              "data-testid": `inactiveCoverCard-${toCamelCase(title)}`,
            },
          },
        }}
      >
        <CardBody display="flex" flex={1}>
          <Block display="flex" flex={1}>
            {icon && (
              <Block display={["none", "none", "block"]} marginRight="scale600">
                <CoverSectionIcon icon={icon} />
              </Block>
            )}
            <Block display="flex" flexDirection="column" flex={1} position="relative">
              <Block display="flex" flexDirection="row" flex={1}>
                <Block
                  display="flex"
                  alignItems="flex-start"
                  flexDirection="column"
                  flex={9}
                >
                  <LabelLarge
                    flex={1}
                    marginTop={0}
                    marginRight="scale400"
                    marginBottom="scale600"
                  >
                    {title}
                  </LabelLarge>
                </Block>

                {enableAndStartEdit && (
                  <Button
                    onClick={handleAdd}
                    size="mini"
                    shape="pill"
                    kind="primary"
                    overrides={{ BaseButton: { style: { height: "min-content" } } }}
                    NORMAL_WIDTH_RESET
                    disabled={disabled || disabledByParent}
                  >
                    Add
                  </Button>
                )}
              </Block>

              <Block flex={1}>
                {description && (
                  <ParagraphMedium marginTop={0} color="mono700" flex={1}>
                    {description}
                  </ParagraphMedium>
                )}
              </Block>

              {product?.provider?.logo && (
                <Block
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  alignSelf={isMobile ? "flex-start" : "flex-end"}
                  justifyContent="space-between"
                  padding="scale100"
                >
                  <LabelXSmall color="mono700" $style={{ whiteSpace: "nowrap" }}>
                    Underwritten by
                  </LabelXSmall>
                  <Block maxWidth={["75px", "75px", "90px"]} marginTop="scale100">
                    <img
                      src={product.provider.logo}
                      alt={product.provider.name}
                      width="100%"
                      style={{ verticalAlign: "middle" }}
                    />
                  </Block>
                </Block>
              )}
            </Block>
          </Block>
        </CardBody>
      </Card>
    </Block>
  );
};

export { InactiveCoverSection };
